import { useState, useContext, useCallback } from "react";
import apiRoot from "../../../api/apiRoot";
import { FaoBuilderContext } from "context/FaoBuilderContext";
import { FaoRegimeContext } from "context/FaoRegimeContext";

/**
 * Custom hook for managing user-initiated regime validation operations.
 * Runs background validation to get validation counts and displays results.
 * Handles validation job execution, data formatting, and state management.
 *
 * @returns {Object} Validation hook interface
 * @returns {Object} returns.validationState - Current validation state
 * @returns {boolean} returns.validationState.isValidating - Whether validation UI is active
 * @returns {boolean} returns.validationState.isLoading - Whether a validation operation is in progress
 * @returns {Array} returns.validationState.validateData - Formatted validation results for display
 * @returns {number} returns.validationState.numFlagged - Count of records falling out of regime (quality issues)
 * @returns {string|null} returns.validationState.error - Error message if validation failed
 * @returns {Function} returns.handleNewValidation - Initiates a new user-triggered validation job
 * @returns {Function} returns.handleViewLastValidation - Fetches and displays the last validation results
 */

function useRegimeValidation() {
  const { modelConfig, pollJobRunStatus } = useContext(FaoBuilderContext);
  const { fetchValidateDate, matrices, dimensions_x, dimensions_y } =
    useContext(FaoRegimeContext);
  const [validationState, setValidationState] = useState({
    isValidating: false,
    isLoading: false,
    validateData: [],
    numFlagged: 0,
    error: null,
  });

  /**
   * Formats raw validation data into matrix structure based on configuration objects.
   * Transforms raw validation data into structured matrices for display.
   * Creates 2D arrays for Admits/Enrolls and populates them from raw data.
   *
   * @param {Array} data - Raw validation data from API
   * @param {Object} data[].matrices_alias - Matrix identifier
   * @param {string} data[].x_axis_range - X-axis dimension range
   * @param {string} data[].y_axis_range - Y-axis dimension range
   * @param {number} data[].Admits - Admission count for this cell
   * @param {number} data[].Enrolls - Enrollment count for this cell
   * @returns {Array} Formatted matrix data with populated Admits/Enrolls arrays for display
   */
  const formatValidateData = useCallback(
    (data) => {
      // Copy matrices config and create 2D arrays for Admits/Enrolls based on x/y dimensions
      const validateMatrix = JSON.parse(JSON.stringify(matrices));
      for (let i = 0; i < validateMatrix.length; i++) {
        validateMatrix[i]["Admits"] = new Array(dimensions_y.length)
          .fill()
          .map(() => Array(dimensions_x.length).fill(null));
        validateMatrix[i]["Enrolls"] = new Array(dimensions_y.length)
          .fill()
          .map(() => Array(dimensions_x.length).fill(null));
      }
      // Find the corresponding cell in raw validation data by matching matrix alias and dimension ranges
      for (let i = 0; i < validateMatrix.length; i++) {
        let matrix = validateMatrix[i];
        for (let j = 0; j < dimensions_y.length; j++) {
          let y_dim = dimensions_y[j];
          for (let k = 0; k < dimensions_x.length; k++) {
            let x_dim = dimensions_x[k];

            const cellValue = data.find(
              (item) =>
                // if matrix name is not given then the alias is set to default_alias in databricks and it won't match here
                (item["matrices_alias"] === matrix.name ||
                  matrix.name === "") &&
                item["x_axis_range"] === x_dim.join("-") &&
                item["y_axis_range"] === y_dim.join("-")
            );
            // if matching cell found, update Admits/Enrolls for that cell
            if (cellValue) {
              validateMatrix[i].Admits[j][k] = cellValue["Admits"];
              validateMatrix[i].Enrolls[j][k] = cellValue["Enrolls"];
            }
          }
        }
      }
      return validateMatrix;
    },
    [dimensions_x, dimensions_y, matrices]
  );

  /**
   * Calculates the total number of records falling out of regime (quality issues).
   * Sums up admission counts for all records marked as regime quality issues.
   *
   * @param {Array} data - Validation data array
   * @param {string} data[].Data_Quality_Flagged - "true" if record is falling out of regime
   * @param {string|number} data[].Admits - Number of admits for flagged record
   * @returns {number} Total count of admissions falling out of regime
   */
  const calculateFlaggedRecords = useCallback((data) => {
    return data
      .filter((item) => item["Data_Quality_Flagged"] === "true")
      .reduce((acc, cur) => {
        return parseInt(cur["Admits"]) + acc;
      }, 0);
  }, []);

  /**
   * Polls the status of a validation job until completion (up to 5 minutes).
   * Uses context function pollJobRunStatus which handles the 5-minute polling logic.
   * Updates validation state with error if job fails.
   *
   * @param {string} runId - Unique identifier for the validation job
   * @returns {Promise<Object|undefined>} Job status response with {runId, runStatus, success} or undefined if failed
   */
  const getValidationStatus = async (runId) => {
    const response = await pollJobRunStatus(runId);
    if (response.success) {
      return response;
    } else {
      setValidationState((prev) => ({
        ...prev,
        isLoading: false,
        error: response.message,
      }));
    }
    return;
  };

  /**
   * Fetches validation results from the API and formats them for display.
   * Updates validation state with formatted data and count of records falling out of regime.
   *
   * @async
   * @throws {Error} When API request fails or data formatting errors occur
   */
  const fetchValidation = useCallback(async () => {
    try {
      const response = await apiRoot.get("/api/fao-regime/get-validation", {
        params: { modelId: modelConfig.id },
      });
      const formattedData = formatValidateData(response.data);
      const flaggedRecords = calculateFlaggedRecords(response.data);
      setValidationState((prev) => ({
        ...prev,
        validateData: formattedData,
        numFlagged: flaggedRecords,
        isLoading: false,
      }));
    } catch (error) {
      console.error("Validation error:", error);
      setValidationState((prev) => ({
        ...prev,
        isLoading: false,
        error: "Error fetching the validation",
      }));
    }
  }, [modelConfig.id, formatValidateData, calculateFlaggedRecords]);

  /**
   * Initiates a new user-triggered validation job and waits for completion.
   * Sets validation state to loading, starts the background validation job,
   * polls for completion (up to 5 minutes), then fetches and formats the results for display.
   *
   * @async
   * @throws {Error} When validation job fails to start or complete
   */
  const handleNewValidation = async () => {
    setValidationState((prev) => ({
      ...prev,
      isValidating: true,
      isLoading: true,
      error: null,
    }));
    try {
      const response = await apiRoot.post(
        "/api/fao-regime/validate",
        {},
        {
          params: { modelId: modelConfig.id },
        }
      );
      if (response.data.runStatus !== "RUNNING") {
        setValidationState((prev) => ({
          ...prev,
          isLoading: false,
          error: response.data.message,
        }));
      } else {
        const runStatus = await getValidationStatus(response.data.runId);
        if (runStatus) {
          await fetchValidation();
          await fetchValidateDate();
        }
      }
    } catch (error) {
      console.log(error);
      setValidationState((prev) => ({
        ...prev,
        isLoading: false,
        error: "Error running the validation job",
      }));
    }
  };

  /**
   * Displays the most recent validation results without running a new validation.
   * Sets validation UI to active state and fetches existing validation data.
   */
  const handleViewLastValidation = () => {
    setValidationState((prev) => ({
      ...prev,
      isValidating: true,
      isLoading: true,
    }));
    fetchValidation();
  };

  return {
    validationState,
    handleNewValidation,
    handleViewLastValidation,
  };
}

export default useRegimeValidation;
